<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="notifications" :style="{ '--total': messages.length }">
    <transition-group name="fade-group">
      <div
        v-for="(msg, msgIndex) in messages"
        :key="msgIndex"
        :class="{
          _msg: true,
          '--error': msg.type == 'error',
          'error-message': msg.type == 'error',
          'success-message': msg.type != 'error',
        }"
        :style="{ '--i': msgIndex }"
      >
        <div
          v-if="msg.title"
          class="_title"
          v-html="sanitizeNotificationHtml(msg.title)"
        ></div>
        <div
          v-if="msg.text"
          class="_text"
          v-html="sanitizeNotificationHtml(msg.text)"
        ></div>
      </div>
    </transition-group>
  </div>
</template>

<script setup>
import { computed, watch, onMounted, defineOptions } from "vue"
import { sanitizeHtml } from "@/utils/sanitize"
import { useStore } from "vuex"
import logger from "@/services/logger"
import {
  NOTIFICATION_MOUNT_CLEAR,
  NOTIFICATION_WATCH_TRIGGER,
} from "@/constants/events"

// LLM:METADATA
// :topology: [Domain:UI | Role:Notification_System | Pattern:Observer]
// :relates-to: uses: "store/modules/ui", required-by: "App.vue"
// :rationale: "Displays global notifications and auto-dismisses them. Cleans up stale notifications on mount to handle edge cases from localStorage restoration."
// :contract: pre: "Component mounted", post: "Stale notifications cleared, auto-dismiss active", invariant: "Messages are cleared after 5 seconds"
// :decision_cache: "Added onMounted cleanup for deterministic notification reset [FIX-NOTIF-CLEANUP-001]"
// LLM:END

defineOptions({
  name: "NotificationsQueue",
  inheritAttrs: false,
})

const store = useStore()
const messages = computed(() => store.getters.getNotifications())
const sanitizeNotificationHtml = (value) => sanitizeHtml(value || "")

// LLM:METADATA
// :topology: [Domain:UI | Role:Notification_System | Pattern:LifecycleHook]
// :relates-to: uses: "store.dispatch", required-by: "Notifications.vue"
// :rationale: "Clears stale notifications on component mount. This handles edge cases where notifications might be restored from localStorage before persistedstate removal takes effect, or from stale storage data."
// :contract: pre: "Component mounted", post: "Notification queue cleared", invariant: "No stale notifications visible after mount"
// :decision_cache: "Defensive cleanup ensures clean state even with stale storage [NOTIF-EDGE-001]"
// LLM:END
onMounted(() => {
  // Clear any stale notifications that might have been restored from localStorage
  // This is a defensive measure to handle edge cases
  if (messages.value.length > 0) {
    logger.info(
      NOTIFICATION_MOUNT_CLEAR,
      "Clearing stale notifications on mount",
      {
        count: messages.value.length,
        notifications: messages.value,
      }
    )
    store.dispatch("setNotifications", [])
  }
})

watch(messages, (newVal) => {
  if (newVal.length) {
    logger.debug(
      NOTIFICATION_WATCH_TRIGGER,
      "Notifications watcher triggered",
      {
        count: newVal.length,
      }
    )

    // Clear notifications after 5 seconds
    setTimeout(() => {
      // Clear all notifications - this matches legacy behavior
      // The setTimeout creates a fresh call frame, so we get current state
      store.dispatch("setNotifications", [])
    }, 5000)
  }
})
</script>

<style lang="scss">
.notifications {
  position: fixed;
  top: 40px;
  right: 20px;
  z-index: 1001;
  ._msg {
    font-size: 1.6rem;
    color: #fff;
    background-color: #639fab;
    box-shadow: 0 0 18px #ccc;
    padding: 30px;
    border-radius: 12px;
    &.--error {
      background-color: #ff4b3e;
    }
  }
  ._msg + ._msg {
    margin-top: 20px;
  }
}
</style>
